import { NolBridge } from '@yanolja-org/nol-bridge';

import { nolBridgeProvider } from './providers/nolBridge';

import type { AdapterEnv, NavigationProvider, Route, RouteHandlerFn } from './types';

/**
 * env 별 origin 매핑 table — `{env}-nol.yanolja.com` 패턴 (prod 만 prefix 없음).
 * `local` 은 dev origin 으로 fallback.
 */
const ENV_ORIGINS: Record<AdapterEnv, { nol: string; platform: string }> = {
  prod: { nol: 'https://nol.yanolja.com', platform: 'https://platform-site.yanolja.com' },
  stage: { nol: 'https://stage-nol.yanolja.com', platform: 'https://stage-platform-site.yanolja.com' },
  dev: { nol: 'https://dev-nol.yanolja.com', platform: 'https://dev-platform-site.yanolja.com' },
  qa: { nol: 'https://qa-nol.yanolja.com', platform: 'https://qa-platform-site.yanolja.com' },
  local: { nol: 'https://dev-nol.yanolja.com', platform: 'https://dev-platform-site.yanolja.com' },
};

/**
 * NOL 표준 navigation routes — env 별 full URL 매핑.
 *
 * 호스트가 일부 route 만 갈아끼우려면:
 *   `{ ...createDefaultRoutes(env), my: '/custom-my' }`
 */
export function createDefaultRoutes(env: AdapterEnv): Record<string, Route> {
  const origin = ENV_ORIGINS[env];
  return {
    logo: origin.nol,
    my: `${origin.nol}/mypage`,
    wishlist: `${origin.nol}/member/wishlist`,
    cart: `${origin.platform}/cart`,
    recent: `${origin.nol}/recent-product/list?target=HOME`,
  };
}

/**
 * 호스트가 `providers` 미명시 시 사용되는 default chain.
 *
 * SPA navigation (react-router / tanstack / next router 등) 은 *호스트가 handlers 에서 직접 처리*
 * — adapter 는 NOL app webview (nolBridge) + 일반 full-page navigation (hrefProvider) 만 흡수.
 *
 * `createActionHandler` 는 항상 마지막에 `hrefProvider` 를 자동 append.
 */
export function createDefaultProviders(): NavigationProvider[] {
  return [nolBridgeProvider];
}

/**
 * 비-URL action 의 default handler 묶음.
 *
 * - `back`: NolBridge 환경이면 `closeWebView()` (앱 webview 액티비티 종료),
 *           아니면 `window.history.back()` fallback.
 *
 * `createActionHandler` 가 `...DEFAULT_HANDLERS, ...config.handlers` 로 자동 merge.
 * 호스트가 `handlers.back` 명시 시 override.
 */
const closeOrBack = (): void => {
  if (NolBridge.isEnabled) {
    void NolBridge.closeWebView();
    return;
  }
  if (typeof window !== 'undefined') {
    window.history.back();
  }
};

export const DEFAULT_HANDLERS: Record<string, RouteHandlerFn> = {
  back: closeOrBack,
  close: closeOrBack,
};
